import { useState } from "react";
import { Mail, Phone, Instagram, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { contactInfo, contactFormInitial, services } from "../../data/mock";
import { toast } from "sonner";

export const ContactSection = () => {
  const [formData, setFormData] = useState({ ...contactFormInitial });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // MOCK: Simulates backend submission - will be replaced with real API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    toast.success("¡Mensaje enviado correctamente!", {
      description: "Nos pondremos en contacto contigo pronto.",
    });

    setFormData({ ...contactFormInitial });
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contacto" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 bg-[#080815]" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(0,119,255,0.03)_0%,transparent_65%)]" />
      <div className="absolute top-20 left-0 w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(0,200,255,0.02)_0%,transparent_65%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <span className="text-sm font-semibold text-[#0077FF] tracking-widest uppercase">
            Contacto
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-white mt-4 leading-tight">
            ¿Tenés un proyecto?{" "}
            <span className="text-white/30">Hablemos</span>
          </h2>
          <p className="text-white/35 mt-5 text-lg leading-relaxed">
            Contános sobre tu idea y te ayudamos a hacerla realidad.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2.5">
                  <Label className="text-white/50 text-sm font-medium">
                    Nombre *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Tu nombre completo"
                    required
                    className="bg-white/[0.025] border-white/[0.07] text-white placeholder:text-white/15 focus:border-[#0077FF]/40 focus:ring-1 focus:ring-[#0077FF]/20 rounded-lg h-12 transition-colors duration-300"
                  />
                </div>
                <div className="space-y-2.5">
                  <Label className="text-white/50 text-sm font-medium">
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="bg-white/[0.025] border-white/[0.07] text-white placeholder:text-white/15 focus:border-[#0077FF]/40 focus:ring-1 focus:ring-[#0077FF]/20 rounded-lg h-12 transition-colors duration-300"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2.5">
                  <Label className="text-white/50 text-sm font-medium">
                    Teléfono
                  </Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="Tu teléfono"
                    className="bg-white/[0.025] border-white/[0.07] text-white placeholder:text-white/15 focus:border-[#0077FF]/40 focus:ring-1 focus:ring-[#0077FF]/20 rounded-lg h-12 transition-colors duration-300"
                  />
                </div>
                <div className="space-y-2.5">
                  <Label className="text-white/50 text-sm font-medium">
                    Servicio de interés
                  </Label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => handleChange("service", value)}
                  >
                    <SelectTrigger className="bg-white/[0.025] border-white/[0.07] text-white h-12 rounded-lg focus:border-[#0077FF]/40 focus:ring-1 focus:ring-[#0077FF]/20 transition-colors duration-300 [&>span]:text-white/15 [&[data-state=open]>span]:text-white">
                      <SelectValue placeholder="Seleccionar servicio" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0e0e20] border-white/[0.08] rounded-lg">
                      {services.map((s) => (
                        <SelectItem
                          key={s.id}
                          value={s.id}
                          className="text-white/70 focus:bg-[#0077FF]/15 focus:text-white rounded-md cursor-pointer"
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2.5">
                <Label className="text-white/50 text-sm font-medium">
                  Mensaje *
                </Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder="Contános sobre tu proyecto o consulta..."
                  required
                  rows={5}
                  className="bg-white/[0.025] border-white/[0.07] text-white placeholder:text-white/15 focus:border-[#0077FF]/40 focus:ring-1 focus:ring-[#0077FF]/20 rounded-lg resize-none transition-colors duration-300"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#0077FF] hover:bg-[#0060DD] text-white px-8 h-12 text-base rounded-lg transition-colors duration-300 w-full sm:w-auto disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Enviar Mensaje
                    <Send size={16} />
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Contact info sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <div className="p-7 rounded-2xl bg-white/[0.015] border border-white/[0.06] space-y-6">
              <h3 className="text-lg font-semibold text-white">
                Información de Contacto
              </h3>

              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-4 text-white/40 hover:text-white/80 transition-colors duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#0077FF]/[0.07] border border-[#0077FF]/15 flex items-center justify-center group-hover:bg-[#0077FF]/[0.12] transition-colors duration-300">
                  <Phone size={17} className="text-[#0077FF]" />
                </div>
                <div>
                  <p className="text-[11px] text-white/25 tracking-wider uppercase font-medium">
                    Teléfono
                  </p>
                  <p className="text-sm mt-0.5">{contactInfo.phone}</p>
                </div>
              </a>

              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-4 text-white/40 hover:text-white/80 transition-colors duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#0077FF]/[0.07] border border-[#0077FF]/15 flex items-center justify-center group-hover:bg-[#0077FF]/[0.12] transition-colors duration-300">
                  <Mail size={17} className="text-[#0077FF]" />
                </div>
                <div>
                  <p className="text-[11px] text-white/25 tracking-wider uppercase font-medium">
                    Email
                  </p>
                  <p className="text-sm mt-0.5">{contactInfo.email}</p>
                </div>
              </a>

              <a
                href={contactInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-white/40 hover:text-white/80 transition-colors duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#0077FF]/[0.07] border border-[#0077FF]/15 flex items-center justify-center group-hover:bg-[#0077FF]/[0.12] transition-colors duration-300">
                  <Instagram size={17} className="text-[#0077FF]" />
                </div>
                <div>
                  <p className="text-[11px] text-white/25 tracking-wider uppercase font-medium">
                    Instagram
                  </p>
                  <p className="text-sm mt-0.5">{contactInfo.instagram}</p>
                </div>
              </a>
            </div>

            {/* Quick info card */}
            <div className="p-7 rounded-2xl bg-[#0077FF]/[0.03] border border-[#0077FF]/[0.08]">
              <h4 className="text-sm font-semibold text-white mb-3">
                Respuesta rápida
              </h4>
              <p className="text-sm text-white/35 leading-relaxed">
                Respondemos todas las consultas dentro de las 24 horas
                hábiles. Para urgencias, contactános directamente por
                teléfono.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
